import React, { useEffect, useMemo, useState } from "react";
import { Banknote, AlertTriangle, Lock } from "lucide-react";
import { toast } from "../ui/Toast";
import SalaryCycleControls from "./SalaryCycleControls";

// Admin salary-review CALIBRATION tab. Reuses SalaryCycleControls for cycle
// list + create/open/close + selection, then shows the ORG-WIDE allocation grid
// (GET /cycles/{id}/allocations — ALL managers' rows, which /my-team can't give)
// grouped by manager, with the perf score beside each increment for fairness
// calibration + an org-wide budget-vs-actual rollup.
//
// EDITABLE while the cycle is OPEN (admin is in the patch_allocation gate;
// calibration = adjusting), read-only once closed. When CLOSED, a DEFERRED
// "pending payroll integration" panel renders — the apply button is a disabled
// placeholder and /apply is NEVER called (payroll is paused).

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const BASE = `${API_BASE}/api/payroll/salary-review`;
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const cellInput = "w-24 bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";

const STATUS_STYLE = {
  pending:   "text-white/50 bg-white/5 border-white/15",
  submitted: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  approved:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  applied:   "text-blue-400 bg-blue-500/10 border-blue-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending: "Pending", submitted: "Submitted", approved: "Approved", applied: "Applied" };

const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));
const money = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

export default function SalaryCalibrationTab() {
  const [cycle, setCycle]     = useState(null);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits]     = useState({});
  const [dirty, setDirty]     = useState({});
  const [rowErr, setRowErr]   = useState({});
  const [savingId, setSavingId] = useState(null);

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const editable = cycle?.status === "open";

  const load = async () => {
    if (!cycle?.id) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/cycles/${cycle.id}/allocations`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setEdits(Object.fromEntries(list.map(r => [r.id, { pct: r.proposed_increase_pct ?? "" }])));
      setDirty({}); setRowErr({});
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cycle?.id]); // eslint-disable-line

  const setEdit = (id, v) => {
    setEdits(e => ({ ...e, [id]: { pct: v } }));
    setDirty(d => ({ ...d, [id]: true }));
  };

  const saveRow = async (row) => {
    if (!dirty[row.id] || !editable) return;
    const pct = edits[row.id]?.pct;
    setSavingId(row.id);
    try {
      const res = await fetch(`${BASE}/allocations/${row.id}`, {
        method: "PATCH", headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ proposed_increase_pct: pct === "" ? null : Number(pct) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 422) {                       // budget guard — surface, keep editable
        const msg = data.detail || "Team increase exceeds the cycle budget";
        setRowErr(r => ({ ...r, [row.id]: msg }));
        toast.error(msg);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(rs => rs.map(r => r.id === row.id
        ? { ...r, proposed_new_salary: data.proposed_new_salary, status: data.status,
            proposed_increase_pct: pct === "" ? null : Number(pct),
            proposed_increase_amount: num(r.current_salary) * num(pct) / 100 }
        : r));
      setRowErr(r => { const n = { ...r }; delete n[row.id]; return n; });
      setDirty(d => { const n = { ...d }; delete n[row.id]; return n; });
      toast.success("Saved");
    } catch { toast.error("Failed to save allocation"); }
    finally { setSavingId(null); }
  };

  // Org-wide budget rollup (live from edit state). Advisory: the backend's 422
  // guard is per-MANAGER; this org-wide figure is a calibration indicator.
  const rollup = useMemo(() => {
    let inc = 0, cur = 0;
    for (const r of rows) {
      const c = num(r.current_salary);
      inc += c * num(edits[r.id]?.pct) / 100;
      cur += c;
    }
    const pct = cur ? (inc / cur) * 100 : 0;
    const budget = cycle?.total_budget_pct != null ? Number(cycle.total_budget_pct) : null;
    return { pct, budget, over: budget != null && pct > budget };
  }, [rows, edits, cycle]);

  // Group rows by manager (route already orders manager→user).
  const groups = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const key = r.manager_id || "none";
      let g = out.find(x => x.key === key);
      if (!g) { g = { key, name: r.manager_name || "No manager", rows: [] }; out.push(g); }
      g.rows.push(r);
    }
    return out;
  }, [rows]);

  // Deferred-apply summary (only relevant on a closed cycle).
  const applySummary = useMemo(() => {
    const eligible = rows.filter(r => ["submitted", "approved"].includes(r.status) && r.proposed_new_salary != null);
    const total = eligible.reduce((s, r) => s + num(r.proposed_new_salary), 0);
    return { count: eligible.length, total };
  }, [rows]);

  return (
    <div className="space-y-5">
      <SalaryCycleControls selectedCycleId={cycle?.id} onSelectCycle={setCycle} />

      {!cycle ? (
        <div className={`${card} p-6 text-white/25 text-sm italic text-center`}>Select a cycle above to calibrate allocations.</div>
      ) : (
        <>
          {/* Org-wide budget rollup */}
          {rollup.budget != null && (
            <div className={`${card} px-5 py-3 flex items-center justify-between gap-3 flex-wrap`}>
              <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Org Budget</span>
              <span className={`text-sm px-3 py-1 rounded-full border ${rollup.over ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-white/10 bg-white/5 text-white/60"}`}>
                Allocated {rollup.pct.toFixed(1)}% / budget {rollup.budget}%{rollup.over ? " — OVER" : ""}
              </span>
            </div>
          )}

          {/* Calibration grid */}
          <div className={`${card} overflow-hidden`}>
            <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
              <Banknote size={14} className="text-[#00ff90]" />
              <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Calibration — {cycle.name}</span>
              {!editable && <span className="text-xs text-white/25">· {cycle.status} (read-only)</span>}
            </div>

            {loading ? (
              <div className="p-5 space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-9 rounded-lg bg-white/5 animate-pulse" />)}</div>
            ) : rows.length === 0 ? (
              <p className="px-5 py-8 text-white/20 text-sm italic text-center">No allocations in this cycle{cycle.status === "draft" ? " — open the cycle to populate them." : "."}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#00ff90]/40 uppercase tracking-wider border-b border-white/5">
                      <th className="px-4 py-2 font-medium">Employee</th>
                      <th className="px-4 py-2 font-medium">Score</th>
                      <th className="px-4 py-2 font-medium text-right">Current</th>
                      <th className="px-4 py-2 font-medium">Increase %</th>
                      <th className="px-4 py-2 font-medium text-right">New Salary</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map(g => (
                      <React.Fragment key={g.key}>
                        <tr className="bg-white/3 border-b border-white/5">
                          <td colSpan={6} className="px-4 py-1.5 text-xs text-[#00ff90]/50 uppercase tracking-wider">{g.name}</td>
                        </tr>
                        {g.rows.map(r => {
                          const e = edits[r.id] || {};
                          const liveNew = num(r.current_salary) * (1 + num(e.pct) / 100);
                          const err = rowErr[r.id];
                          return (
                            <tr key={r.id} className={`border-b border-white/5 ${err ? "bg-red-500/5" : ""}`}>
                              <td className="px-4 py-2 text-white/80">{r.employee_name || r.user_id.slice(0, 8)}</td>
                              <td className="px-4 py-2">
                                {r.perf_score != null || r.perf_rating
                                  ? <span className="text-xs text-white/50">{r.perf_rating || "—"}{r.perf_score != null ? ` (${r.perf_score})` : ""}</span>
                                  : <span className="text-xs text-white/20">—</span>}
                              </td>
                              <td className="px-4 py-2 text-right text-white/60">{money(r.current_salary)}</td>
                              <td className="px-4 py-2">
                                {editable ? (
                                  <input type="number" className={`${cellInput} ${err ? "border-red-500/50" : ""}`}
                                    value={e.pct} disabled={savingId === r.id}
                                    onChange={ev => setEdit(r.id, ev.target.value)}
                                    onBlur={() => saveRow(r)} />
                                ) : (
                                  <span className="text-white/60">{r.proposed_increase_pct != null ? `${r.proposed_increase_pct}%` : "—"}</span>
                                )}
                                {err && <p className="text-xs text-red-400 mt-0.5 max-w-[180px]">{err}</p>}
                              </td>
                              <td className="px-4 py-2 text-right text-white/70">{editable && e.pct !== "" ? money(liveNew) : money(r.proposed_new_salary)}</td>
                              <td className="px-4 py-2">{statusBadge(r.status)}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Deferred apply panel — only on a closed cycle */}
          {cycle.status === "closed" && (
            <div className={`${card} p-5 space-y-3`}>
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-amber-400" />
                <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Ready to Apply</span>
              </div>
              <p className="text-sm text-white/60">
                <span className="text-white/80 font-medium">{applySummary.count}</span> allocation{applySummary.count === 1 ? "" : "s"} to be applied · total new payroll{" "}
                <span className="text-white/80 font-medium">{money(applySummary.total)}</span>
              </p>
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
                <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200/80">
                  Pending payroll integration — increments are recorded; applying to compensation will be enabled once payroll is connected.
                </p>
              </div>
              {/* DISABLED placeholder — never wired to POST /apply. */}
              <button type="button" disabled aria-disabled="true"
                className="text-xs px-5 py-2 rounded-lg border border-white/10 bg-white/5 text-white/30 cursor-not-allowed">
                Apply to payroll (pending integration)
              </button>
            </div>
          )}

          {cycle.status === "applied" && (
            <div className={`${card} p-5 text-sm text-white/40`}>This cycle has been applied to payroll.</div>
          )}
        </>
      )}
    </div>
  );
}
